/**
 * Deployment-environment contract — the one place that says which compute a
 * given environment owns and which data/integration profile it borrows.
 *
 * Four environments exist. Three are the established ones; the fourth,
 * **Sandbox**, is a persistent explicitly-deployed environment for exercising a
 * selected branch before it is on the staging → production path. Sandbox owns
 * its own compute (Workers, `sandbox_` Functions, `sandbox-` Cloud Run
 * services, `-sandbox` Cloud Tasks queues, `sandbox-audit-job`) but shares
 * staging's data layer and integration profile by decision — see
 * ai-dev/dev/sandbox-env/sandbox-env-plan+tracker.md §1.
 *
 * Two concerns are deliberately kept apart:
 *
 *   - `DeploymentEnvironment` — the execution identity. Decides compute names,
 *     API/Functions targets, queues, jobs, build identity, deployment records.
 *   - `DataProfile` / `IntegrationProfile` — which database labels, buckets,
 *     KV/R2 namespaces, provider accounts and modes are in use. Sandbox maps to
 *     `staging` here on purpose; that mapping is the ONLY place the borrowing
 *     is expressed, so no consumer has to invent it.
 *
 * `StripeEnvironment` (`billing.ts`) already uses the word `sandbox` for Stripe
 * test mode. That is a *payment mode*, not a deployment environment, and the
 * two must never be conflated: payment mode is derived from the integration
 * profile below, never the other way round.
 *
 * Every helper here is a pure function of its inputs so it can be unit-tested
 * against the exact strings the providers see. Consumers must not concatenate
 * prefixes themselves.
 */

export const DEPLOYMENT_ENVIRONMENTS = [
  'production',
  'staging',
  'sandbox',
  'development',
] as const;

export type DeploymentEnvironment = (typeof DEPLOYMENT_ENVIRONMENTS)[number];

/** Environments that run on hosted infrastructure (never a developer laptop or emulator). */
export const HOSTED_DEPLOYMENT_ENVIRONMENTS = ['production', 'staging', 'sandbox'] as const;

export type HostedDeploymentEnvironment = (typeof HOSTED_DEPLOYMENT_ENVIRONMENTS)[number];

/**
 * The persisted-data label vocabulary the platform already uses (`env` stamps on
 * Firestore documents, GCS object-key suffixes, BigQuery row columns). Sandbox
 * writes `staging` because it shares staging's data.
 */
export type DataProfile = 'prod' | 'staging' | 'dev';

/** Provider-account/mode selection: Clerk instance, Stripe mode, email restrictions. */
export type IntegrationProfile = 'production' | 'staging' | 'development';

export function isDeploymentEnvironment(value: unknown): value is DeploymentEnvironment {
  return (
    typeof value === 'string' &&
    (DEPLOYMENT_ENVIRONMENTS as readonly string[]).includes(value)
  );
}

export function isHostedDeploymentEnvironment(
  value: unknown
): value is HostedDeploymentEnvironment {
  return (
    typeof value === 'string' &&
    (HOSTED_DEPLOYMENT_ENVIRONMENTS as readonly string[]).includes(value)
  );
}

/**
 * Parse a configured environment string (`ENVIRONMENT` on Workers,
 * `A7Y_DEPLOYMENT_ENVIRONMENT` on Functions). Anything unrecognised is
 * `development` — the historical behaviour for an unset value — EXCEPT that a
 * caller that must refuse to run on an unknown hosted value should use
 * `isDeploymentEnvironment` first and fail loudly.
 */
export function parseDeploymentEnvironment(value: unknown): DeploymentEnvironment {
  return isDeploymentEnvironment(value) ? value : 'development';
}

/** Sandbox borrows staging's data; everything else keeps its own label. */
export function dataProfileFor(env: DeploymentEnvironment): DataProfile {
  switch (env) {
    case 'production':
      return 'prod';
    case 'staging':
    case 'sandbox':
      return 'staging';
    case 'development':
      return 'dev';
  }
}

/** Sandbox borrows staging's provider accounts, modes, and restrictions. */
export function integrationProfileFor(env: DeploymentEnvironment): IntegrationProfile {
  switch (env) {
    case 'production':
      return 'production';
    case 'staging':
    case 'sandbox':
      return 'staging';
    case 'development':
      return 'development';
  }
}

// ---------------------------------------------------------------------------
// Compute identity
// ---------------------------------------------------------------------------

/**
 * JS-export prefix applied by `a7y-functions/scripts/prefix-functions.mjs`
 * (`staging_asoAudit`, `sandbox_asoAudit`). Production exports are bare.
 * Development resolves to the staging prefix because a developer's emulator and
 * ad-hoc callers have always targeted staging-shaped names off-prod.
 */
export function functionExportPrefix(env: DeploymentEnvironment): '' | 'staging_' | 'sandbox_' {
  switch (env) {
    case 'production':
      return '';
    case 'staging':
    case 'development':
      return 'staging_';
    case 'sandbox':
      return 'sandbox_';
  }
}

/**
 * The `FUNCTION_PREFIX` value the build/deploy pipeline sets (hyphenated; the
 * prefix script normalises it to the underscore form above).
 */
export function functionPrefixEnvValue(env: DeploymentEnvironment): '' | 'staging-' | 'sandbox-' {
  return functionExportPrefix(env).replace('_', '-') as '' | 'staging-' | 'sandbox-';
}

/**
 * Cloud Run derives its service name from the export id by lowercasing, and
 * `_` becomes `-` because Cloud Run names cannot contain underscores.
 * `staging_asoAuditWorker` → `staging-asoauditworker`.
 */
export function cloudRunServicePrefix(env: DeploymentEnvironment): '' | 'staging-' | 'sandbox-' {
  return functionExportPrefix(env).replace(/_/g, '-') as '' | 'staging-' | 'sandbox-';
}

export function cloudRunServiceName(env: DeploymentEnvironment, functionName: string): string {
  return `${cloudRunServicePrefix(env)}${functionName}`.toLowerCase();
}

export function functionExportName(env: DeploymentEnvironment, functionName: string): string {
  return `${functionExportPrefix(env)}${functionName}`;
}

/**
 * Cloud Tasks queue suffix (`audit-jobs-prod`, `audit-jobs-staging`,
 * `audit-jobs-sandbox`). Development enqueues onto staging's queues, as it
 * always has.
 */
export function cloudTasksQueueSuffix(env: DeploymentEnvironment): 'prod' | 'staging' | 'sandbox' {
  switch (env) {
    case 'production':
      return 'prod';
    case 'staging':
    case 'development':
      return 'staging';
    case 'sandbox':
      return 'sandbox';
  }
}

export function cloudTasksQueueName(env: DeploymentEnvironment, queueBaseName: string): string {
  return `${queueBaseName}-${cloudTasksQueueSuffix(env)}`;
}

/**
 * Cloud Run Job name for the long-run audit lane (`audit-job`,
 * `staging-audit-job`, `sandbox-audit-job`).
 */
export function auditJobName(env: DeploymentEnvironment, base = 'audit-job'): string {
  return `${cloudRunServicePrefix(env)}${base}`;
}

/**
 * Artifact Registry tag for the audit Job image: `<sha>` for production,
 * `staging-<sha>` / `sandbox-<sha>` otherwise, so one commit's images per
 * environment are distinct artifacts.
 */
export function auditJobImageTag(env: DeploymentEnvironment, sha: string): string {
  const prefix = cloudRunServicePrefix(env);
  return prefix === '' ? sha : `${prefix}${sha}`;
}

/** Firebase Functions codebase (`firebase.<codebase>.json`). Production is the default codebase. */
export function firebaseCodebase(env: DeploymentEnvironment): 'default' | 'staging' | 'sandbox' {
  switch (env) {
    case 'production':
      return 'default';
    case 'staging':
    case 'development':
      return 'staging';
    case 'sandbox':
      return 'sandbox';
  }
}

// ---------------------------------------------------------------------------
// Public origins
// ---------------------------------------------------------------------------

const ORIGINS: Record<HostedDeploymentEnvironment, { webapp: string; api: string }> = {
  production: { webapp: 'https://www.apptonomy.ai', api: 'https://api.apptonomy.ai' },
  staging: {
    webapp: 'https://www-staging.apptonomy.ai',
    api: 'https://api-staging.apptonomy.ai',
  },
  sandbox: {
    webapp: 'https://www-sandbox.apptonomy.ai',
    api: 'https://api-sandbox.apptonomy.ai',
  },
};

export function webappOrigin(env: HostedDeploymentEnvironment): string {
  return ORIGINS[env].webapp;
}

export function apiOrigin(env: HostedDeploymentEnvironment): string {
  return ORIGINS[env].api;
}

export function webappHostname(env: HostedDeploymentEnvironment): string {
  return new URL(ORIGINS[env].webapp).hostname;
}

export function apiHostname(env: HostedDeploymentEnvironment): string {
  return new URL(ORIGINS[env].api).hostname;
}

/**
 * Classify a request hostname. Returns `undefined` for anything that is not one
 * of the three deployed webapp hosts — callers must decide what an unknown host
 * means (local dev, a `*.workers.dev` preview) rather than defaulting to
 * production, which is the exact fall-through this contract exists to remove.
 */
export function deploymentEnvironmentFromWebappHost(
  host: string
): HostedDeploymentEnvironment | undefined {
  const hostname = host.split(':')[0].toLowerCase();
  for (const env of HOSTED_DEPLOYMENT_ENVIRONMENTS) {
    if (hostname === webappHostname(env)) return env;
  }
  return undefined;
}

/**
 * Deployed hostnames whose affiliate/visitor cookies must stay host-only. The
 * production host legitimately spans `www.` and `api.` with a parent-domain
 * cookie; every other deployed subdomain must not, or a Sandbox/staging session
 * cookie would be presented to production hosts.
 */
export function cookieMustBeHostOnly(host: string): boolean {
  const env = deploymentEnvironmentFromWebappHost(host);
  return env === 'staging' || env === 'sandbox';
}

// ---------------------------------------------------------------------------
// Build identity
// ---------------------------------------------------------------------------

/**
 * Safe build metadata every deployed surface exposes (frontend `version.json`,
 * API `/health`, a Functions status endpoint) so `sandbox status` and the
 * environment banner can identify the served build without cloud credentials.
 * Stamped from pinned build inputs, never from request parameters.
 */
export interface BuildIdentity {
  deploymentEnvironment: DeploymentEnvironment;
  /** Full immutable git SHA of the deployed source, or `unknown` when unstamped. */
  sha: string;
  /** Branch label recorded at deploy time (informational; the SHA is the identity). */
  branch: string;
  /** ISO-8601 build timestamp, or `unknown`. */
  builtAt: string;
}

export const UNKNOWN_BUILD_VALUE = 'unknown';

export function buildIdentity(input: {
  deploymentEnvironment: unknown;
  sha?: string | null;
  branch?: string | null;
  builtAt?: string | null;
}): BuildIdentity {
  const clean = (v: string | null | undefined): string => {
    const t = typeof v === 'string' ? v.trim() : '';
    return t === '' ? UNKNOWN_BUILD_VALUE : t;
  };
  return {
    deploymentEnvironment: parseDeploymentEnvironment(input.deploymentEnvironment),
    sha: clean(input.sha),
    branch: clean(input.branch),
    builtAt: clean(input.builtAt),
  };
}
